import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Container,
  Section,
  ContentContainer,
  Value,
  ErrorValue,
  Label,
  ResultItem,
  ResultKey,
  CollectionCard,
  CollectionRow,
  CollectionInfo,
  CollectionTitle,
  CollectionMeta,
  CollectionBadge,
  CollectionsEmpty,
  CollectionRadio,
  SearchArea,
  SearchButton,
  ImportFooter,
  ConfirmBanner,
  BeneficiaryName,
} from './styles'
import FormTitle from '@components/text/formTitle'
import TextInput from '@components/inputs/textInput'
import PrimaryButton from '@components/inputs/button/primaryButton'
import SecondaryButton from '@components/inputs/button/secondaryButton'
import Loading from '@components/loading'
import { getDateStringFromUsableDateStr } from '@util/timeUtil'
import { cpfMask } from '@util/stringUtil'
import {
  getCandidateCollections as getCandidateCollectionsAction,
  importErrorToCollection as importErrorToCollectionAction,
} from '@actions/exam'

const hasCollectionLabData = (col) => [
  col.glucose,
  col.ldlCholesterol,
  col.hdlCholesterol,
  col.triglycerides,
  col.totalCholesterol,
  col.notHdl,
  col.vldlCholesterol,
].some((v) => v != null)

const LabExamDetailsModal = ({
  isOpen,
  setClose,
  exam,
  onImported,
  getCandidateCollections,
  importErrorToCollection,
}) => {
  const [collections, setCollections] = useState(null)
  const [beneficiaryName, setBeneficiaryName] = useState(null)
  const [loadingCollections, setLoadingCollections] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [importing, setImporting] = useState(false)
  const [cpfInput, setCpfInput] = useState(cpfMask(exam.cpf) || '')

  const formattedDate = getDateStringFromUsableDateStr(exam.importedAt)
  const isError = Boolean(exam.error)

  const parseCollectedAt = (value) => moment(value, 'DD/MM/YYYY HH:mm:ss')

  const searchCollections = (cpf) => {
    const cleanCpf = (cpf || '').replace(/\D/g, '')
    if (!cleanCpf) return
    setLoadingCollections(true)
    setCollections(null)
    setBeneficiaryName(null)
    setSelectedId(null)
    setIsConfirming(false)
    getCandidateCollections(cleanCpf, (data) => {
      let list = []
      if (Array.isArray(data?.collections)) list = data.collections
      else if (Array.isArray(data)) list = data
      const sorted = list.slice().sort((a, b) => {
        const da = parseCollectedAt(a.collectedAt).valueOf()
        const db = parseCollectedAt(b.collectedAt).valueOf()
        return db - da
      })
      setCollections(sorted)
      setBeneficiaryName(data?.beneficiaryName || null)
      setLoadingCollections(false)
    })
  }

  useEffect(() => {
    if (isError && exam.cpf) {
      searchCollections(exam.cpf)
    }
  }, [exam.cpf, isError])

  const selectedCollection = (collections || []).find((c) => c.id === selectedId) || null

  const doImport = () => {
    if (!selectedId) return
    setImporting(true)
    importErrorToCollection(exam.id, selectedId, (result) => {
      setImporting(false)
      setIsConfirming(false)
      if (result) {
        onImported && onImported()
        setClose()
      }
    })
  }

  const onImportClick = () => {
    if (!selectedCollection) return
    if (hasCollectionLabData(selectedCollection)) {
      setIsConfirming(true)
    } else {
      doImport()
    }
  }

  const renderCollection = (collection) => {
    const parsed = collection.collectedAt ? parseCollectedAt(collection.collectedAt) : null
    const collectedAt = parsed && parsed.isValid()
      ? parsed.format('DD/MM/YYYY [às] HH:mm')
      : '—'
    const containsData = hasCollectionLabData(collection)
    const isSelected = selectedId === collection.id

    return (
      <CollectionCard
        key={collection.id}
        selected={isSelected}
        onClick={() => !importing && setSelectedId(collection.id)}
      >
        <CollectionRow>
          <CollectionRadio selected={isSelected} />
          <CollectionInfo>
            <CollectionTitle>{`Coleta #${collection.id}`}</CollectionTitle>
            <CollectionMeta>{collectedAt}</CollectionMeta>
          </CollectionInfo>
          {containsData && <CollectionBadge>Contém dados</CollectionBadge>}
        </CollectionRow>
      </CollectionCard>
    )
  }

  return (
    <Container isOpen={isOpen}>
      <Section>
        <FormTitle
          title="Detalhes do exame importado"
          subtitle={`Importado em ${formattedDate}`}
          onClickGoBack={setClose}
          style={{ fontFamily: 'Circular-Std', flex: 'none' }}
        >
          Detalhes do exame importado
        </FormTitle>
        <ContentContainer>
          <Label>
            Nome do beneficiário
          </Label>
          <Value>
            {exam.name}
          </Value>
          <Label>
            Tipo de exame
          </Label>
          <Value>
            Exame laboratorial
          </Value>
          {exam.error && (
            <>
              <Label>
                Mensagem de erro
              </Label>
              <ErrorValue>
                {exam.error}
              </ErrorValue>
            </>
          )}
          <Label>
            Resultados
          </Label>
          <Value>
            {exam.glucose != null && (
            <ResultItem>
              <ResultKey>Glicose:</ResultKey>
              {' '}
              {exam.glucose}
            </ResultItem>
            )}
            {exam.hdlCholesterol != null && (
            <ResultItem>
              <ResultKey>HDL Colesterol:</ResultKey>
              {' '}
              {exam.hdlCholesterol}
            </ResultItem>
            )}
            {exam.ldlCholesterol != null && (
            <ResultItem>
              <ResultKey>LDL Colesterol:</ResultKey>
              {' '}
              {exam.ldlCholesterol}
            </ResultItem>
            )}
            {exam.notHdlCholesterol != null && (
            <ResultItem>
              <ResultKey>Colesterol não-HDL:</ResultKey>
              {' '}
              {exam.notHdlCholesterol}
            </ResultItem>
            )}
            {exam.totalCholesterol != null && (
            <ResultItem>
              <ResultKey>Colesterol Total:</ResultKey>
              {' '}
              {exam.totalCholesterol}
            </ResultItem>
            )}
            {exam.triglycerides != null && (
            <ResultItem>
              <ResultKey>Triglicerídeos:</ResultKey>
              {' '}
              {exam.triglycerides}
            </ResultItem>
            )}
            {exam.vldlCholesterol != null && (
              <ResultItem>
                <ResultKey>VLDL Colesterol:</ResultKey>
                {' '}
                {exam.vldlCholesterol}
              </ResultItem>
            )}
            {exam.error && ![
              exam.glucose,
              exam.hdlCholesterol,
              exam.ldlCholesterol,
              exam.notHdlCholesterol,
              exam.totalCholesterol,
              exam.triglycerides,
              exam.vldlCholesterol
            ].some((value) => value != null) && (
              <ResultItem>
                <ResultKey>Detalhes:</ResultKey>
                {' '}
                Nenhum resultado extraído do exame.
              </ResultItem>
            )}
          </Value>
          {isError && (
            <>
              <Label>
                Importar dados para uma coleta existente
              </Label>
              <SearchArea>
                <TextInput
                  placeholder="CPF do beneficiário"
                  value={cpfInput}
                  onChange={(e) => setCpfInput(cpfMask(e.target.value))}
                  maxLength={14}
                />
                <SearchButton
                  onClick={() => searchCollections(cpfInput)}
                  disabled={loadingCollections || !(cpfInput || '').replace(/\D/g, '')}
                >
                  {loadingCollections ? <Loading color="white" height={16} width={16} /> : 'Buscar'}
                </SearchButton>
              </SearchArea>
              {!loadingCollections && collections && collections.length === 0 && (
                <CollectionsEmpty>
                  Nenhuma coleta encontrada para este CPF.
                </CollectionsEmpty>
              )}
              {!loadingCollections && collections && collections.length > 0 && (
                <>
                  {beneficiaryName && (
                    <BeneficiaryName>{beneficiaryName}</BeneficiaryName>
                  )}
                  <div>
                    {collections.map(renderCollection)}
                  </div>
                  {isConfirming && (
                    <ConfirmBanner>
                      Esta coleta já possui dados laboratoriais. Deseja sobrescrever?
                    </ConfirmBanner>
                  )}
                  <ImportFooter>
                    {isConfirming ? (
                      <>
                        <SecondaryButton
                          onClick={() => setIsConfirming(false)}
                          disabled={importing}
                        >
                          Cancelar
                        </SecondaryButton>
                        <PrimaryButton
                          onClick={doImport}
                          disabled={importing}
                        >
                          {importing ? <Loading color="white" height={16} width={16} /> : 'Sobrescrever'}
                        </PrimaryButton>
                      </>
                    ) : (
                      <PrimaryButton
                        onClick={onImportClick}
                        disabled={!selectedId || importing}
                      >
                        {importing ? <Loading color="white" height={16} width={16} /> : 'Importar exame para coleta selecionada'}
                      </PrimaryButton>
                    )}
                  </ImportFooter>
                </>
              )}
            </>
          )}
        </ContentContainer>
      </Section>
    </Container>
  )
}

LabExamDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setClose: PropTypes.func.isRequired,
  exam: PropTypes.shape({
    id: PropTypes.number.isRequired,
    importedAt: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    cpf: PropTypes.string,
    error: PropTypes.string,
    glucose: PropTypes.number,
    hdlCholesterol: PropTypes.number,
    ldlCholesterol: PropTypes.number,
    notHdlCholesterol: PropTypes.number,
    totalCholesterol: PropTypes.number,
    triglycerides: PropTypes.number,
    vldlCholesterol: PropTypes.number
  }).isRequired,
  onImported: PropTypes.func,
  getCandidateCollections: PropTypes.func.isRequired,
  importErrorToCollection: PropTypes.func.isRequired,
}

LabExamDetailsModal.defaultProps = {
  onImported: null,
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getCandidateCollections: getCandidateCollectionsAction,
  importErrorToCollection: importErrorToCollectionAction,
}, dispatch)

export default connect(null, mapDispatchToProps)(LabExamDetailsModal)
